import React from "react";
import { useShow, useList } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Button, Card, Table, Tag, Tabs, Spin } from "antd";
import { useParams, Link, useNavigate } from "react-router-dom";
import Column from "antd/es/table/Column";
import { 
    ShopOutlined, 
    ShoppingCartOutlined, 
    FileTextOutlined,
    EditOutlined,
    StarFilled,
    PlusOutlined,
    CheckCircleOutlined,
    MergeCellsOutlined,
    StopOutlined,
    ExportOutlined,
    BookOutlined,
} from "@ant-design/icons";
import { VendorAccountingTab } from "../../components/VendorAccountingTab";
import { PageHeader } from "../../components/ui";

interface VendorContact {
    id: number;
    phoneNumber: string;
    email: string;
    isPrimary: boolean;
}

interface VendorAddress {
    id: number;
    houseNo: string;
    street: string;
    city: string;
    province: string;
    country: string;
    isPrimary: boolean;
}

interface Vendor {
    id: number;
    tradeName: string;
    vatNumber: string;
    tpin: string;
    name: string;
    description?: string;
    taxNumber: string;
    bankAccount: string;
    bankName: string;
    swiftCode: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    vendorContacts: VendorContact[];
    vendorAddresses: VendorAddress[];
}

export const VendorShow: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { queryResult } = useShow({
        resource: "vendors",
        id,
        meta: {
            fields: [
                "id",
                "tradeName",
                "vatNumber",
                "tpin",
                "name",
                "description",
                "bankAccount",
                "bankName",
                "swiftCode",
                "isActive",
                "createdAt",
                "updatedAt",
                {
                    vendorContacts: [
                        "id",
                        "phoneNumber",
                        "email",
                        "isPrimary"
                    ]
                },
                {
                    vendorAddresses: [
                        "id",
                        "houseNo",
                        "street",
                        "city",
                        "province",
                        "country",
                        "isPrimary"
                    ]
                }
            ]
        }
    });
    const { data, isLoading } = queryResult;
    const record = data?.data as Vendor | undefined;

    const { data: purchasesData, isLoading: purchasesLoading } = useList({
        resource: "purchases",
        filters: record?.id ? [{ field: "vendorId", operator: "eq", value: record.id }] : [],
        queryOptions: { enabled: !!record?.id },
        meta: {
            fields: [
                "id",
                "orderNumber",
                "totalAmount",
                "status",
                "transactionDate"
            ]
        }
    });

    const { data: purchaseOrdersData, isLoading: purchaseOrdersLoading } = useList({
        resource: "purchaseorders",
        filters: record?.id ? [{ field: "vendorId", operator: "eq", value: record.id }] : [],
        queryOptions: { enabled: !!record?.id },
        meta: {
            fields: [
                "id",
                "orderNumber",
                "totalAmount",
                "status",
                "orderDate"
            ]
        }
    });

    // Calculate lifetime purchases
    const lifetimePurchases = purchasesData?.data?.reduce((sum: number, purchase: any) => sum + (purchase.totalAmount || 0), 0) || 0;

    const getStatusColor = (status: string) => {
        switch (status) {
            case "Draft":
                return "blue";
            case "Confirmed":
            case "Completed":
                return "green";
            case "Cancelled":
                return "red";
            case "Converted":
                return "purple";
            default:
                return "default";
        }
    };

    const moreMenuItems = [
        {
            key: "merge",
            label: "Merge Vendor",
            icon: <MergeCellsOutlined />,
            onClick: () => console.log("Merge vendor"),
        },
        {
            key: "deactivate",
            label: record?.isActive ? "Deactivate" : "Activate",
            icon: <StopOutlined />,
            onClick: () => console.log("Toggle active"),
        },
        {
            key: "export",
            label: "Export Statement",
            icon: <ExportOutlined />,
            onClick: () => console.log("Export statement"),
        },
        {
            key: "ledger",
            label: "View Ledger",
            icon: <BookOutlined />,
            onClick: () => console.log("View ledger"),
        },
    ];

    const tabItems = [
        {
            key: 'overview',
            label: 'Overview',
            children: (
                <div className="space-y-4">
                    {/* Vendor Information Card */}
                    <div className="info-card">
                        <div className="info-card-header">
                            <h3 className="info-card-title">Vendor Information</h3>
                        </div>
                        <div className="info-card-body">
                            <div className="info-grid">
                                <div className="info-item">
                                    <span className="info-label">Trade Name:</span>
                                    <span className="info-value">{record?.tradeName || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">VAT Number:</span>
                                    <span className="info-value">{record?.vatNumber || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Legal Name:</span>
                                    <span className="info-value">{record?.name || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">TIN Number:</span>
                                    <span className="info-value">{record?.tpin || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Bank Name:</span>
                                    <span className="info-value">{record?.bankName || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Bank Account:</span>
                                    <span className="info-value">{record?.bankAccount || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Swift Code:</span>
                                    <span className="info-value">{record?.swiftCode || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Status:</span>
                                    <span className={`status-badge ${record?.isActive ? 'active' : 'inactive'}`}>
                                        {record?.isActive && <CheckCircleOutlined />}
                                        {record?.isActive ? "Active" : "Inactive"}
                                    </span>
                                </div>
                                <div className="info-item full-width">
                                    <span className="info-label">Description:</span>
                                    <span className="info-value">{record?.description || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Created At:</span>
                                    <span className="info-value">
                                        <DateField value={record?.createdAt} format="YYYY-MM-DD HH:mm:ss" />
                                    </span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Last Updated:</span>
                                    <span className="info-value">
                                        <DateField value={record?.updatedAt} format="YYYY-MM-DD HH:mm:ss" />
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contacts and Addresses Row */}
                    <div className="two-column-grid">
                        {/* Contacts Card */}
                        <Card 
                            title="Contacts" 
                            className="data-table-card"
                            extra={
                                <Button 
                                    type="primary" 
                                    size="small" 
                                    icon={<EditOutlined />}
                                >
                                    Add Contact
                                </Button>
                            }
                        >
                            <Table
                                dataSource={record?.vendorContacts}
                                rowKey={(record) => String(record.id)}
                                pagination={false}
                                size="small"
                            >
                                <Column dataIndex="phoneNumber" title="Phone Number" />
                                <Column dataIndex="email" title="Email" />
                                <Column
                                    dataIndex="isPrimary"
                                    title="Primary"
                                    render={(value: boolean) => (
                                        value ? <StarFilled className="primary-star" /> : null
                                    )}
                                />
                                <Column
                                    title=""
                                    width={40}
                                    render={() => (
                                        <EditOutlined className="edit-icon-btn" />
                                    )}
                                />
                            </Table>
                        </Card>

                        {/* Addresses Card */}
                        <Card 
                            title="Addresses" 
                            className="data-table-card"
                            extra={
                                <Button 
                                    type="primary" 
                                    size="small" 
                                    icon={<EditOutlined />}
                                >
                                    Add Address
                                </Button>
                            }
                        >
                            <Table
                                dataSource={record?.vendorAddresses}
                                rowKey={(record) => String(record.id)}
                                pagination={false}
                                size="small"
                            >
                                <Column dataIndex="houseNo" title="House No" />
                                <Column dataIndex="street" title="Street" />
                                <Column dataIndex="city" title="City" />
                                <Column dataIndex="province" title="Province" />
                                <Column dataIndex="country" title="Country" />
                                <Column
                                    title=""
                                    width={40}
                                    render={() => (
                                        <EditOutlined className="edit-icon-btn" />
                                    )}
                                />
                            </Table>
                        </Card>
                    </div>

                    {/* Full Width Addresses Table */}
                    <Card 
                        title="Addresses" 
                        className="data-table-card"
                        extra={
                            <Button 
                                type="text" 
                                size="small" 
                                icon={<PlusOutlined />}
                                className="action-btn-outlined"
                            >
                                Add Address
                            </Button>
                        }
                    >
                        <Table
                            dataSource={record?.vendorAddresses}
                            rowKey={(record) => String(record.id)}
                            pagination={false}
                            size="small"
                        >
                            <Column dataIndex="houseNo" title="House No" />
                            <Column dataIndex="street" title="Street" />
                            <Column dataIndex="city" title="City" />
                            <Column dataIndex="province" title="Province" />
                            <Column dataIndex="country" title="Country" />
                            <Column
                                dataIndex="isPrimary"
                                title="Primary"
                                render={(value: boolean) => (
                                    value ? <Tag color="blue">Primary</Tag> : null
                                )}
                            />
                            <Column
                                title=""
                                width={40}
                                render={() => (
                                    <EditOutlined className="edit-icon-btn" />
                                )}
                            />
                        </Table>
                    </Card>
                </div>
            ),
        },
        {
            key: 'transactions',
            label: 'Transactions',
            children: (
                <div className="space-y-4">
                    <Card title="Purchases" className="data-table-card" loading={purchasesLoading}>
                        <Table
                            dataSource={purchasesData?.data}
                            rowKey={(record) => String(record.id)}
                            pagination={{ pageSize: 10 }}
                        >
                            <Column
                                dataIndex="orderNumber"
                                title="Order Number"
                                render={(text: string, record: any) => (
                                    <Link to={`/purchases/show/${record.id}`}>{text}</Link>
                                )}
                            />
                            <Column
                                dataIndex="totalAmount"
                                title="Total Amount"
                                render={(value: number) => `$${value?.toFixed(2)}`}
                            />
                            <Column
                                dataIndex="status"
                                title="Status"
                                render={(status: string) => (
                                    <Tag color={getStatusColor(status)}>{status}</Tag>
                                )}
                            />
                            <Column
                                dataIndex="transactionDate"
                                title="Transaction Date"
                                render={(value: string) => (
                                    <DateField value={value} format="YYYY-MM-DD" />
                                )}
                            />
                        </Table>
                    </Card>

                    <Card title="Purchase Orders" className="data-table-card" loading={purchaseOrdersLoading}>
                        <Table
                            dataSource={purchaseOrdersData?.data}
                            rowKey={(record) => String(record.id)}
                            pagination={{ pageSize: 10 }}
                        >
                            <Column
                                dataIndex="orderNumber"
                                title="Order Number"
                                render={(text: string, record: any) => (
                                    <Link to={`/purchaseorders/show/${record.id}`}>{text}</Link>
                                )}
                            />
                            <Column
                                dataIndex="totalAmount"
                                title="Total Amount"
                                render={(value: number) => `$${value?.toFixed(2)}`}
                            />
                            <Column
                                dataIndex="status"
                                title="Status"
                                render={(status: string) => (
                                    <Tag color={getStatusColor(status)}>{status}</Tag>
                                )}
                            />
                            <Column
                                dataIndex="orderDate"
                                title="Order Date"
                                render={(value: string) => (
                                    <DateField value={value} format="YYYY-MM-DD" />
                                )}
                            />
                        </Table>
                    </Card>
                </div>
            ),
        },
        {
            key: 'details',
            label: 'Vendor Details',
            children: (
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Full Vendor Details</h3>
                        <Button 
                            type="primary" 
                            icon={<EditOutlined />}
                            onClick={() => navigate(`/vendors/edit/${id}`)}
                        >
                            Edit
                        </Button>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Trade Name:</span>
                                <span className="info-value">{record?.tradeName || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Legal Name:</span>
                                <span className="info-value">{record?.name || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">VAT Number:</span>
                                <span className="info-value">{record?.vatNumber || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">TIN Number:</span>
                                <span className="info-value">{record?.tpin || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Bank Name:</span>
                                <span className="info-value">{record?.bankName || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Bank Account:</span>
                                <span className="info-value">{record?.bankAccount || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Swift Code:</span>
                                <span className="info-value">{record?.swiftCode || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Status:</span>
                                <span className={`status-badge ${record?.isActive ? 'active' : 'inactive'}`}>
                                    {record?.isActive && <CheckCircleOutlined />}
                                    {record?.isActive ? "Active" : "Inactive"}
                                </span>
                            </div>
                            <div className="info-item full-width">
                                <span className="info-label">Description:</span>
                                <span className="info-value">{record?.description || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Created At:</span>
                                <span className="info-value">
                                    <DateField value={record?.createdAt} format="YYYY-MM-DD HH:mm:ss" />
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Last Updated:</span>
                                <span className="info-value">
                                    <DateField value={record?.updatedAt} format="YYYY-MM-DD HH:mm:ss" />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            key: 'accounting',
            label: 'Accounting',
            children: record?.id ? <VendorAccountingTab vendorId={record.id} /> : null,
        },
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="show-page-wrapper">
            <PageHeader
                title={record?.tradeName || "Vendor"}
                titleIcon={<ShopOutlined />}
                breadcrumbs={[
                    { title: "Vendors", href: "/vendors" },
                    { title: "Show Vendor" },
                ]}
                actions={[
                    {
                        label: "New Purchase",
                        icon: <ShoppingCartOutlined />,
                        href: `/purchases/create?vendorId=${id}`,
                        variant: "outlined",
                    },
                    {
                        label: "New Purchase Order",
                        icon: <FileTextOutlined />,
                        href: `/purchaseorders/create?vendorId=${id}`,
                        variant: "outlined",
                    },
                ]}
                moreMenuItems={moreMenuItems}
            >
                {/* Vendor-specific mini stat cards */}
                <div className="mini-stats-row">
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Lifetime Purchases</div>
                        <div className="mini-stat-value">${lifetimePurchases.toFixed(2)}</div>
                    </div>
                </div>
            </PageHeader>

            <Tabs defaultActiveKey="overview" items={tabItems} />
        </div>
    );
};
